import { useState } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { copyToClipboard } from "@/lib/utils";
import { Copy, RefreshCw } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

const LOREM_WORDS = [
  "lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit",
  "sed", "do", "eiusmod", "tempor", "incididunt", "ut", "labore", "et", "dolore",
  "magna", "aliqua", "enim", "ad", "minim", "veniam", "quis", "nostrud", "exercitation",
  "ullamco", "laboris", "nisi", "ut", "aliquip", "ex", "ea", "commodo", "consequat",
  "duis", "aute", "irure", "dolor", "in", "reprehenderit", "in", "voluptate", "velit",
  "esse", "cillum", "dolore", "eu", "fugiat", "nulla", "pariatur", "excepteur", "sint",
  "occaecat", "cupidatat", "non", "proident", "sunt", "in", "culpa", "qui", "officia",
  "deserunt", "mollit", "anim", "id", "est", "laborum"
];

export function LoremIpsumGenerator() {
  const [count, setCount] = useState(3);
  const [type, setType] = useState<"paragraphs" | "sentences" | "words">("paragraphs");
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const generateWord = () => LOREM_WORDS[Math.floor(Math.random() * LOREM_WORDS.length)];

  const generateSentence = () => {
    const length = Math.floor(Math.random() * 10) + 5;
    let sentence = [];
    for (let i = 0; i < length; i++) {
      sentence.push(generateWord());
    }
    const finalStr = sentence.join(" ");
    return finalStr.charAt(0).toUpperCase() + finalStr.slice(1) + ".";
  };

  const generateParagraph = () => {
    const length = Math.floor(Math.random() * 5) + 3;
    let paragraph = [];
    for (let i = 0; i < length; i++) {
      paragraph.push(generateSentence());
    }
    return paragraph.join(" ");
  };

  const generateText = () => {
    let result = [];
    const actualCount = count || 1;
    
    if (type === "paragraphs") {
      for (let i = 0; i < actualCount; i++) result.push(generateParagraph());
      setOutput(result.join("\n\n"));
    } else if (type === "sentences") {
      for (let i = 0; i < actualCount; i++) result.push(generateSentence());
      setOutput(result.join(" "));
    } else {
      for (let i = 0; i < actualCount; i++) result.push(generateWord());
      const words = result.join(" ");
      setOutput(words.charAt(0).toUpperCase() + words.slice(1) + ".");
    }
  };

  // Generate initial content
  useState(() => generateText());

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Lorem Ipsum Generator"
      description="Generate placeholder text (Lorem Ipsum) for design mockups, layouts, and testing. Choose paragraphs, sentences, or words."
      h1="Lorem Ipsum Generator"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Choose 'Paragraphs', 'Sentences', or 'Words'.",
        "Set the amount you want to generate.",
        "Click 'Generate' to get your placeholder text."
      ]}
      faq={[
        { question: "What is Lorem Ipsum?", answer: "Lorem Ipsum is placeholder text used in design and printing to demonstrate visual layout." },
        { question: "How to use Lorem Ipsum for design?", answer: "Use it to fill content areas in design mockups to focus on the overall layout." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-end gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Amount</label>
            <Input 
              type="number" 
              min="1" 
              max="100" 
              value={count} 
              onChange={(e) => setCount(Number(e.target.value))}
              className="w-24 bg-white"
            />
          </div>
          
          <div className="flex bg-slate-200 p-1 rounded-md">
            {(["paragraphs", "sentences", "words"] as const).map(t => (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors capitalize ${
                  type === t ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <Button onClick={generateText} className="ml-auto">
            <RefreshCw className="h-4 w-4 mr-2" />
            Generate
          </Button>
        </div>

        <Textarea
          className="min-h-[400px] text-base leading-relaxed bg-slate-50"
          value={output}
          readOnly
        />

        <div className="flex gap-2">
          <Button onClick={handleCopy} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Output"}
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
